import { useReducer, useEffect, useCallback, useMemo } from 'react';

import { fetchWithTokenAndRegion } from '@adsk/acs-sc-web-platform-api';

const FETCH_ACTIONS = {
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  SET_DATA: 'SET_DATA',
};

const CONFIG_ACTIONS = {
  SET_CONFIG: 'SET_CONFIG',
  MANUAL_SET_CONFIG: 'MANUAL_SET_CONFIG',
  REFRESH: 'REFRESH',
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const dataFetchReducer = (state, action) => {
  switch (action.type) {
    case FETCH_ACTIONS.FETCH_INIT:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case FETCH_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        data: action.payload,
      };
    case FETCH_ACTIONS.FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case FETCH_ACTIONS.SET_DATA:
      return {
        ...state,
        data: action.payload,
      };
    default:
      throw new Error();
  }
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const configReducer = (state, action) => {
  switch (action.type) {
    case CONFIG_ACTIONS.SET_CONFIG:
      return {
        ...state,
        prevConfig: action.payload,
        config: action.payload,
      };
    case CONFIG_ACTIONS.MANUAL_SET_CONFIG:
      return {
        ...state,
        config: action.payload,
        requestId: state.requestId + 1,
      };
    case CONFIG_ACTIONS.REFRESH:
      return {
        ...state,
        requestId: state.requestId + 1,
      };
    default:
      throw new Error();
  }
};

/**
 * useDataApi hook can be used to fetch data and get the request status easily. This hook adds `Authorization` and `Region` header using `fetchWithTokenAndRegion`.
 * A new request would be sent each time:
 *  - `url` or `fetchData` are changed
 *  - calling `refresh`
 *  - calling `doFetch`
 *
 * If you need request to initiate only upon manual interaction, pass an empty `url` and call `doFetch` with the needed request URL & data.
 * You can use `setData` to set the data manually.
 *
 * @param url - the URL to send request to.
 * @param fetchData - additional data for `window.fetch` if needed.
 *
 * @returns {{loading: boolean, data: null, error: null, refresh: *, doFetch: *, setData: *}}
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useDataApi = (url?: string, fetchData?: unknown) => {
  const [fetchState, dispatchFetchAction] = useReducer(dataFetchReducer, {
    loading: false,
    error: null,
    data: null,
  });
  const config = useMemo(() => ({ url, fetchData }), [url, fetchData]);
  const [configState, dispatchConfigAction] = useReducer(configReducer, {
    config,
    prevConfig: config,
    requestId: 0,
  });

  useEffect(() => {
    // Check if hook's args were changed. In that case we need to initiate a new request.
    dispatchConfigAction({ type: CONFIG_ACTIONS.SET_CONFIG, payload: config });
  }, [config]);

  useEffect(() => {
    if (!configState.config.url) return;

    dispatchFetchAction({ type: FETCH_ACTIONS.FETCH_INIT });

    let cancel = false;
    fetchWithTokenAndRegion(
      configState.config.url,
      configState.config.fetchData,
    )
      .then((response) => {
        if (!cancel) {
          dispatchFetchAction({
            type: FETCH_ACTIONS.FETCH_SUCCESS,
            payload: response,
          });
        }
      })
      .catch((error) => {
        if (!cancel) {
          dispatchFetchAction({
            type: FETCH_ACTIONS.FETCH_FAILURE,
            payload: error,
          });
        }
      });
    // eslint-disable-next-line consistent-return
    return () => {
      cancel = true;
    };
  }, [configState.config, configState.requestId]);

  const refresh = useCallback(
    () => dispatchConfigAction({ type: CONFIG_ACTIONS.REFRESH }),
    [],
  );

  const doFetch = useCallback(
    (url, fetchData) =>
      dispatchConfigAction({
        type: CONFIG_ACTIONS.MANUAL_SET_CONFIG,
        payload: { url, fetchData },
      }),
    [],
  );

  const setData = useCallback(
    (data) =>
      dispatchFetchAction({ type: FETCH_ACTIONS.SET_DATA, payload: data }),
    [],
  );

  return {
    ...fetchState,
    refresh,
    setData,
    doFetch,
  };
};

export default useDataApi;
